/**
 * Finish-signup form — collects DoB + role for a freshly-authed user
 * who doesn't yet have a `public.users` row. Runs the age-gate
 * (`checkUkGdprCompliance`) and routes by result:
 *
 *   - UNDER_13_DEFERRED  → render <Under13Deferred /> + sign out
 *   - INFORMED_PARENTAL_NOTICE / OK → INSERT users row, signal complete
 *
 * Replaces the old auto-ensureCoachUser path for real (non-bypass)
 * sign-ins. v1 launch posture (docs/spec/22a-v1-launch-posture.md):
 * under-13 player accounts are not created in v1.
 *
 * Lives inside <AuthGate /> — when session exists but the user row
 * doesn't, AuthGate renders this form instead of the children.
 */
import { useState } from 'react';
import { Button } from './primitives';
import { Under13Deferred } from './Under13Deferred';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { checkUkGdprCompliance } from '@4cm/core/auth';
import './AuthGate.css';

type RoleOption = 'COACH' | 'PLAYER' | 'PARENT' | 'CLUB_ADMIN';

const ROLES: { value: RoleOption; label: string; sub: string }[] = [
  { value: 'COACH', label: 'Coach', sub: 'Plan sessions, rate players, manage teams' },
  { value: 'PLAYER', label: 'Player (13+)', sub: 'Self-rate, track development, talk to Brain' },
  { value: 'PARENT', label: 'Parent', sub: 'See your child\'s progress' },
  { value: 'CLUB_ADMIN', label: 'Club Admin', sub: 'Manage your organisation and teams' },
];

export function FinishSignup({ onComplete }: { onComplete: () => void }) {
  const { user } = useAuth();
  const [dob, setDob] = useState('');
  const [role, setRole] = useState<RoleOption | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deferred, setDeferred] = useState(false);

  if (deferred) {
    return <Under13Deferred />;
  }

  const handleSubmit = async () => {
    setError(null);
    if (!user?.id) {
      setError('Sign-in expired — refresh and try again.');
      return;
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dob)) {
      setError('Enter date of birth as YYYY-MM-DD.');
      return;
    }
    const dobDate = new Date(dob);
    if (Number.isNaN(dobDate.getTime())) {
      setError('That date of birth isn\'t valid.');
      return;
    }
    if (!role) {
      setError('Pick a role to continue.');
      return;
    }

    const status = checkUkGdprCompliance(dobDate);
    if (status.status === 'UNDER_13_DEFERRED') {
      // V1 posture: don't create a users row, sign out, show
      // informational screen. The flag flip in v2 changes the gate's
      // branch — this code path becomes unreachable.
      await supabase.auth.signOut();
      setDeferred(true);
      return;
    }

    setSubmitting(true);
    const email = user.email ?? `${user.id.slice(0, 8)}@4cm.local`;
    const name =
      ((user.user_metadata?.full_name ?? user.user_metadata?.name) as string | undefined)
        ?.trim() ||
      (user.email?.split('@')[0] ?? 'Coach');

    const { error: upsertError } = await supabase.from('users').upsert(
      {
        id: user.id,
        email,
        name,
        role,
        dob,
      },
      { onConflict: 'id' },
    );
    setSubmitting(false);
    if (upsertError) {
      setError(upsertError.message);
      return;
    }
    onComplete();
  };

  return (
    <div className="auth-gate">
      <div className="auth-gate__panel">
        <h1 className="auth-gate__brand">4CM</h1>
        <p className="auth-gate__copy">A couple of details to finish setting up.</p>

        {error && <p className="auth-gate__error">{error}</p>}

        <label className="auth-gate__field">
          <span>Date of birth</span>
          <input
            type="date"
            value={dob}
            onChange={(e) => setDob(e.target.value)}
            max={new Date().toISOString().slice(0, 10)}
            className="auth-gate__input"
          />
        </label>

        <fieldset className="auth-gate__field">
          <legend>I'm a…</legend>
          <div className="auth-gate__roles">
            {ROLES.map((r) => (
              <label key={r.value} className="auth-gate__role">
                <input
                  type="radio"
                  name="role"
                  value={r.value}
                  checked={role === r.value}
                  onChange={() => setRole(r.value)}
                />
                <span className="auth-gate__role-label">{r.label}</span>
                <span className="auth-gate__role-sub">{r.sub}</span>
              </label>
            ))}
          </div>
        </fieldset>

        <Button
          variant="primary"
          size="touchline"
          onClick={() => void handleSubmit()}
        >
          {submitting ? 'Saving…' : 'Continue'}
        </Button>
      </div>
    </div>
  );
}
